import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { acknowledgeNotice, getPublicNotice, getPublicNoticeByCode } from "../../digi/NewMasterSheet/ResidentDirectory/noticesApi";
import "./noticeView.scss";

const formatBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-CA", { day: "numeric", month: "long", year: "numeric" });
};
const isPdf = (a) => /pdf/i.test(a.mime || "") || /\.pdf$/i.test(a.filename || "");
const isImage = (a) => /^image\//i.test(a.mime || "") || /\.(png|jpe?g|webp)$/i.test(a.filename || "");
const isVideo = (a) => /^video\//i.test(a.mime || "") || /\.(mp4|m4v|mov)$/i.test(a.filename || "");
/** Web copies and posters belong to another file; they are never listed on their own. */
const isDerivative = (a) => a.kind === "inline" || a.kind === "poster";

// A resident's phone drops a bar mid-load more often than a link is actually dead. The cache
// remembers the one fact that matters if that happens again later: whether they already told
// us they'd read it. Nothing else about the notice is worth remembering offline.
const cacheKeyFor = (code, id) => `rdc_notice_ack_${code || id || ""}`;
const readAckCache = (code, id) => {
  try {
    const raw = window.localStorage.getItem(cacheKeyFor(code, id));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};
const writeAckCache = (code, id, data) => {
  try {
    window.localStorage.setItem(cacheKeyFor(code, id), JSON.stringify(data));
  } catch (_) {
    // Private browsing / storage disabled: the cache is a nicety, not a requirement.
  }
};

/**
 * What a resident sees from the link in their email: the notice, its message, and the file,
 * readable on a phone without logging in. The token in the address is the authentication.
 */
const NoticeView = () => {
  const { id, token, code } = useParams();
  const [state, setState] = useState({ loading: true, error: "", errorKind: "", data: null });
  const [ack, setAck] = useState({ at: null, busy: false, error: "" });
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Two ways in: the short code from a text message, or the original long link that is still
  // sitting in inboxes from before short links existed. Both land here.
  //
  // A failure with no clean JSON body (a dropped connection, a server mid-restart) is treated
  // as transient and retried once automatically before anything alarming is shown. A failure
  // the server actually explained (an expired or unknown code) is shown as-is; it is real.
  const load = useCallback(
    (isRetry) => {
      const req = code ? getPublicNoticeByCode(code) : getPublicNotice(id, token);
      return req
        .then((res) => {
          if (!mountedRef.current) return;
          if (!res?.success) {
            const err = new Error(res?.message || "This notice could not be loaded.");
            err.__serverMessage = res?.message || "";
            throw err;
          }
          if (res.data?.acknowledged_at) {
            writeAckCache(code, id, { acknowledged_at: res.data.acknowledged_at, title: res.data.title || "" });
          }
          setState({ loading: false, error: "", errorKind: "", data: res.data });
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          const status = err?.response?.status;
          const serverMessage = err?.__serverMessage || (err?.response?.data && typeof err.response.data === "object" ? err.response.data.message : "");
          // Only a status the server meant as final ("that code doesn't exist" / "that token is
          // bad") means the link itself is dead. Everything else — no response at all, a 429, a
          // 500 while the server is mid-restart — is the kind of thing that clears up on its own.
          const isDead = serverMessage && [400, 401, 404].includes(status);
          if (!isDead && !isRetry) {
            // One quiet retry before showing anything. Most of what looks like a dead link is
            // a phone's connection blinking for a second.
            setTimeout(() => { if (mountedRef.current) load(true); }, 1200);
            return;
          }
          const cached = readAckCache(code, id);
          if (cached?.acknowledged_at) {
            setState({ loading: false, error: "", errorKind: "cached-ack", data: cached });
            return;
          }
          setState({
            loading: false,
            errorKind: isDead ? "expired" : "transient",
            error: serverMessage || "We couldn't load your notice just now.",
            data: null,
          });
        });
    },
    [code, id, token]
  );

  useEffect(() => {
    setState({ loading: true, error: "", errorKind: "", data: null });
    load(false);
  }, [load]);

  const retry = () => {
    setState({ loading: true, error: "", errorKind: "", data: null });
    load(false);
  };

  // A notice that asks to be acknowledged remembers the answer, so a resident coming back to
  // the link sees what they already pressed rather than being asked twice.
  useEffect(() => {
    if (state.data?.acknowledged_at) setAck((a) => ({ ...a, at: state.data.acknowledged_at }));
  }, [state.data]);

  const dataRef = useRef(null);
  dataRef.current = state.data;

  const confirmRead = useCallback(async () => {
    setAck((a) => ({ ...a, busy: true, error: "" }));
    try {
      const res = await acknowledgeNotice(code);
      const at = res?.data?.acknowledged_at || new Date().toISOString();
      writeAckCache(code, id, { acknowledged_at: at, title: dataRef.current?.title || "" });
      setAck({ at, busy: false, error: "" });
    } catch (err) {
      setAck((a) => ({ ...a, busy: false, error: "That did not go through. Please try again." }));
    }
  }, [code, id]);

  const n = state.data;
  const shown = (n?.attachments || []).filter((a) => !isDerivative(a));
  const hasFile = shown.length > 0;

  // With no file, there is nothing left to do but confirm reading it, so the page does that the
  // moment it renders rather than making the resident tap a second button for zero new content.
  // This fires from the page's own script, not from the raw link in the email, so an email
  // client's automated safety scan (a plain HTTP fetch, no JavaScript) can never trigger it.
  useEffect(() => {
    if (!n || !code) return;
    if (!n.require_acknowledgement || hasFile) return;
    if (n.acknowledged_at || ack.at || ack.busy) return;
    confirmRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, code, hasFile]);
  const posters = (n?.attachments || []).filter((a) => a.kind === "poster");
  const posterFor = (attachmentId) => posters.find((p) => p.poster_for === attachmentId)?.url || undefined;
  const videos = shown.filter(isVideo);
  const main = shown.find((a) => !isVideo(a));
  const rest = shown.filter((a) => a !== main && !isVideo(a));

  return (
    <div className="nv-page">
      <div className="nv-card">
        {state.loading ? (
          <div className="nv-state">Loading your notice…</div>
        ) : state.errorKind === "cached-ack" ? (
          <div className="nv-state">
            <span className="nv-state-ic is-good">
              <FeatherIcon icon="check-circle" size={22} />
            </span>
            <h1>You're all set</h1>
            <p>
              You already acknowledged{n.title ? ` "${n.title}"` : " this notice"}
              {n.acknowledged_at ? ` on ${formatDate(n.acknowledged_at)}` : ""}. There's nothing else to do here.
            </p>
          </div>
        ) : state.errorKind === "transient" ? (
          <div className="nv-state">
            <span className="nv-state-ic">
              <FeatherIcon icon="wifi-off" size={22} />
            </span>
            <h1>We couldn't load your notice</h1>
            <p>This is usually a connection hiccup, not a dead link.</p>
            <button type="button" className="nv-btn nv-retry-btn" onClick={retry}>
              <FeatherIcon icon="refresh-cw" size={14} /> Try again
            </button>
          </div>
        ) : state.error ? (
          <div className="nv-state">
            <span className="nv-state-ic">
              <FeatherIcon icon="alert-circle" size={22} />
            </span>
            <h1>This link is not available</h1>
            <p>{state.error}</p>
            <p className="nv-muted">Links in notice emails work for 60 days. If you need this notice, reply to the email and the property will send it again.</p>
          </div>
        ) : (
          <>
            <header className="nv-head">
              <p className="nv-eyebrow">{n.type} notice{n.company_name ? ` from ${n.company_name}` : ""}</p>
              <h1>{n.title}</h1>
              <p className="nv-meta">
                {[n.unit_no ? `Unit ${n.unit_no}` : "", n.property_name, n.building_name].filter(Boolean).join(" · ")}
                {n.sent_at ? ` · ${formatDate(n.sent_at)}` : ""}
              </p>
            </header>

            {n.resident_name ? <p className="nv-hi">Hi {n.resident_name},</p> : null}
            {n.message ? <div className="nv-message">{n.message}</div> : null}

            {main ? (
              <section className="nv-doc">
                <div className="nv-doc-head">
                  <span className="nv-doc-name">
                    <FeatherIcon icon="file-text" size={15} /> {main.filename}
                    <small>{formatBytes(main.size)}</small>
                  </span>
                  <a className="nv-btn" href={main.url} download={main.filename} target="_blank" rel="noopener noreferrer">
                    <FeatherIcon icon="download" size={14} /> Download
                  </a>
                </div>
                {isPdf(main) ? (
                  <iframe className="nv-frame" src={main.url} title={main.filename} />
                ) : isImage(main) ? (
                  <img className="nv-image" src={main.url} alt={main.filename} />
                ) : (
                  <p className="nv-muted">Use Download to open this file.</p>
                )}
              </section>
            ) : null}

            {videos.map((v) => (
              <section className="nv-video" key={v.attachment_id}>
                <video
                  className="nv-player"
                  controls
                  preload="metadata"
                  playsInline
                  poster={posterFor(v.attachment_id)}
                  onError={(e) => e.currentTarget.classList.add("is-broken")}
                >
                  <source src={v.url} type={v.mime || "video/mp4"} />
                </video>
                <a className="nv-btn nv-video-dl" href={v.url} download={v.filename} target="_blank" rel="noopener noreferrer">
                  <FeatherIcon icon="download" size={14} /> Download video
                </a>
              </section>
            ))}

            {rest.length > 0 && (
              <section className="nv-extras">
                <p className="nv-eyebrow">Also attached</p>
                {rest.map((a) => (
                  <a key={a.attachment_id} className="nv-extra" href={a.url} download={a.filename} target="_blank" rel="noopener noreferrer">
                    <FeatherIcon icon="paperclip" size={14} /> {a.filename} <small>{formatBytes(a.size)}</small>
                  </a>
                ))}
              </section>
            )}

            {n.require_acknowledgement && code ? (
              <section className="nv-ack">
                {ack.at || n.acknowledged_at ? (
                  <p className="nv-ack-done">
                    <FeatherIcon icon="check-circle" size={16} /> Acknowledged on {formatDate(ack.at || n.acknowledged_at)}
                  </p>
                ) : hasFile ? (
                  <>
                    <button type="button" className="nv-ack-btn" onClick={confirmRead} disabled={ack.busy}>
                      {ack.busy ? "Sending…" : "Got it — I've read this"}
                    </button>
                    {ack.error ? <p className="nv-ack-err">{ack.error}</p> : null}
                  </>
                ) : ack.error ? (
                  <>
                    <button type="button" className="nv-ack-btn" onClick={confirmRead} disabled={ack.busy}>
                      {ack.busy ? "Sending…" : "Try acknowledging again"}
                    </button>
                    <p className="nv-ack-err">{ack.error}</p>
                  </>
                ) : (
                  // Nothing to view, so the page acknowledges on its own the moment it loads
                  // (see the effect above); this is what it looks like while that's in flight.
                  <p className="nv-ack-pending">
                    <FeatherIcon icon="loader" size={16} /> Recording that you've read this…
                  </p>
                )}
              </section>
            ) : null}

            <footer className="nv-foot">
              {n.sender_name ? <p>Sent by {n.sender_name}{n.company_name ? `, ${n.company_name}` : ""}. Reply to the email to reach them.</p> : null}
              <p className="nv-muted">This page is personal to you. Please do not share the link.</p>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export { NoticeView };
export default NoticeView;
